import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFromLocalStorage, setInLocalStorage, removeFromLocalStorage } from '../utils/localStorage';
import { getUserProfile, refreshToken as refreshTokenApi } from '../api/apis';
import { toast } from 'react-toastify';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  // Refresh token function
  const refreshToken = useCallback(async () => {
    try {
      const token = getFromLocalStorage('token');
      if (!token) return null;

      const response = await refreshTokenApi(token);
      if (response.data && response.data.token) {
        const newToken = response.data.token;
        setInLocalStorage('token', newToken);
        return newToken;
      }
      return token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  }, []);

  // Check if token exists and is valid
  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        const token = getFromLocalStorage('token');
        const adminToken = getFromLocalStorage('adminToken');

        // Check Admin Token FIRST
        if (adminToken) {
          if (isMounted) {
            setIsAdmin(true);
            setUser({ role: 'admin' });
          }
          return;
        }

        const expirationTimeStr = localStorage.getItem('expirationTime');
        const expirationTime = expirationTimeStr ? parseInt(expirationTimeStr) : 0;

        // Check if token is expired - ONLY if expiration time is set and valid
        // If expiration is 0 (missing), we assume it might be valid and let getUserProfile verify it
        if (token && expirationTime > 0 && Date.now() > expirationTime) {
          console.log('Token expired locally, attempting refresh...');
          const newToken = await refreshToken();
          if (!newToken) {
            console.warn('Refresh failed for expired token');
            throw new Error('Session expired');
          }
        }

        if (token) {
          // Verify token with backend
          try {
            // Get user info from local storage to pass in headers
            const storedUser = getFromLocalStorage('user');
            const userInfo = storedUser?.emailId || storedUser?.username || storedUser;

            const response = await getUserProfile({
              headers: {
                'User-Info': userInfo
              }
            });
            if (response.data) {
              if (isMounted) {
                setUser(response.data);
                setIsAdmin(false);

                // Map database fields to role for existing users
                const mapRoleFromDatabase = (userData) => {
                  const userType = userData.user_type || 'viewer';
                  const membershipPlan = userData.membershipPlan || 'free';

                  if (userType === 'creator' && membershipPlan === 'pro') {
                    return 'creator_pro';
                  } else if (userType === 'creator' && membershipPlan === 'free') {
                    return 'creator';
                  } else {
                    return 'user';
                  }
                };

                const userRole = mapRoleFromDatabase(response.data);
                localStorage.setItem('userRole', userRole);

                // Update expiration time (24 hours from now) - refresh the session
                const newExpiration = Date.now() + 24 * 60 * 60 * 1000;
                setInLocalStorage('expirationTime', newExpiration.toString());
              }
            } else {
              throw new Error('Invalid token response');
            }
          } catch (err) {
            // If getUserProfile fails, it might be because the token is invalid
            console.error('Profile fetch failed:', err);
            throw err;
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Don't clear tokens on payment callback or overlay pages
        const isPaymentPage = window.location.pathname.includes('payment-callback');
        const isOverlayPage = window.location.pathname.includes('stream') || window.location.pathname.includes('overlay');

        if (!isPaymentPage && !isOverlayPage) {
          // Clear invalid tokens
          removeFromLocalStorage('token');
          removeFromLocalStorage('user');
          removeFromLocalStorage('userRole');
          removeFromLocalStorage('expirationTime');
          if (window.location.pathname.startsWith('/admin')) {
            removeFromLocalStorage('adminToken');
          }
          // Suppress noisy toast if we just logged in or auth checks are disabled
          if (!window.__suppressAuthChecks && !window.location.pathname.includes('login')) {
            // Only show toast if it's a real error, not just a missing token on first load
            if (getFromLocalStorage('token')) {
              toast.error('Your session has expired. Please log in again.');
            }
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    // Set up token refresh interval (every 5 minutes)
    const interval = setInterval(() => {
      const token = getFromLocalStorage('token');
      if (token) {
        refreshToken();
      }
    }, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [refreshToken]);

  const login = (userData, token, isAdmin = false) => {
    if (isAdmin) {
      localStorage.setItem('adminToken', token);
      setIsAdmin(true);
      setUser({ role: 'admin' });
    } else {
      const expirationTime = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('expirationTime', expirationTime.toString());

      // Map database fields to role
      const mapRoleFromDatabase = (userData) => {
        const userType = userData.user_type || 'viewer';
        const membershipPlan = userData.membershipPlan || 'free';

        if (userType === 'creator' && membershipPlan === 'pro') {
          return 'creator_pro';
        } else if (userType === 'creator' && membershipPlan === 'free') {
          return 'creator';
        } else {
          return 'user';
        }
      };

      const userRole = mapRoleFromDatabase(userData);
      localStorage.setItem('userRole', userRole);

      setUser(userData);
      setIsAdmin(false);
    }
  };

  const logout = () => {
    if (isAdmin) {
      localStorage.removeItem('adminToken');
      setIsAdmin(false);
      navigate('/admin/login');
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('expirationTime');
      setUser(null);
      navigate('/login');
    }
  };

  const value = {
    user,
    isAdmin,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};

// AuthContext is now a named export
